import { ref, readonly } from 'vue'

// Global state for the spinner
const isVisible = ref(false)
const message = ref('Please wait...')
const subMessage = ref('')

// Stack to handle multiple concurrent operations
const operationStack = ref<Array<{ id: string; message: string; subMessage?: string }>>([])

export function useGlobalSpinner() {
  
  const show = (msg: string = 'Please wait...', subMsg: string = '') => {
    message.value = msg
    subMessage.value = subMsg
    isVisible.value = true
  }

  const hide = () => {
    isVisible.value = false
    message.value = 'Please wait...'
    subMessage.value = ''
  }

  const showWithId = (id: string, msg: string = 'Please wait...', subMsg: string = '') => {
    // Add to stack
    operationStack.value.push({ id, message: msg, subMessage: subMsg })
    
    // Show the latest operation
    const latest = operationStack.value[operationStack.value.length - 1]
    message.value = latest.message
    subMessage.value = latest.subMessage || ''
    isVisible.value = true
  }

  const hideWithId = (id: string) => {
    // Remove from stack
    operationStack.value = operationStack.value.filter(op => op.id !== id)
    
    if (operationStack.value.length === 0) {
      // No more operations, hide spinner
      isVisible.value = false
      message.value = 'Please wait...'
      subMessage.value = ''
    } else {
      // Show the latest remaining operation
      const latest = operationStack.value[operationStack.value.length - 1]
      message.value = latest.message
      subMessage.value = latest.subMessage || ''
    }
  }

  const updateMessage = (msg: string, subMsg: string = '') => {
    if (isVisible.value) {
      message.value = msg
      subMessage.value = subMsg
      
      // Update the latest operation in stack if using IDs
      if (operationStack.value.length > 0) {
        const latest = operationStack.value[operationStack.value.length - 1]
        latest.message = msg
        latest.subMessage = subMsg
      }
    }
  }

  return {
    // Read-only state
    isVisible: readonly(isVisible),
    message: readonly(message),
    subMessage: readonly(subMessage),
    
    // Methods
    show,
    hide,
    showWithId,
    hideWithId,
    updateMessage
  }
}

// Convenience functions for common operations
export const globalSpinner = {
  show: (message?: string, subMessage?: string) => {
    const { show } = useGlobalSpinner()
    show(message, subMessage)
  },
  
  hide: () => {
    const { hide } = useGlobalSpinner()
    hide()
  },
  
  showForOperation: (id: string, message?: string, subMessage?: string) => {
    const { showWithId } = useGlobalSpinner()
    showWithId(id, message, subMessage)
  },
  
  hideForOperation: (id: string) => {
    const { hideWithId } = useGlobalSpinner()
    hideWithId(id)
  },
  
  updateMessage: (message: string, subMessage?: string) => {
    const { updateMessage } = useGlobalSpinner()
    updateMessage(message, subMessage)
  }
}